import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { CheckIcon } from "../../../assets/Check";
import { cn } from "../../../lib/utils";
import { selectBasketItems } from "../../../store/selectors/basket";
import { impactOccuer } from "../../../utils/hapticEvents";
import { useGetMenu } from "../../home/services/getMenu";
import { IConsumable } from "../../home/types/Menu";
import {
  getStoredOrderExtras,
  OrderExtra,
  saveStoredOrderExtras,
  toOrderExtra,
} from "../libs/orderExtrasStorage";

/**
 * Расходники под списком товаров в корзине.
 *
 * Список не фиксированный: каждый товар приносит с собой свои consumables
 * (у лаваша — салфетки, у чая — сахар), а корзина показывает их объединение.
 * Меню тут нужно потому, что в корзине могут лежать позиции, добавленные до
 * появления consumables — у них поле пустое, а в меню оно уже есть.
 *
 * Выбор живёт только в localStorage, а не в сторе: единственный, кому он нужен,
 * — чекаут, и тот читает его один раз при отправке заказа. Очищается там же,
 * после успешного создания заказа (см. useCreateOrder).
 */
export const OrderExtras = ({ branchId }: { branchId: number }) => {
  const { i18n } = useTranslation();
  const basketItems = useSelector(selectBasketItems);
  const { menu, isLoading: isMenuLoading } = useGetMenu(branchId || undefined);
  const [selected, setSelected] = useState<OrderExtra[]>(getStoredOrderExtras);

  const consumablesByProductId = useMemo(() => {
    const map = new Map<number, IConsumable[]>();

    menu.forEach((category) => {
      category.menu.forEach((product) => {
        if (product.consumables?.length) {
          map.set(product.id, product.consumables);
        }
      });
    });

    return map;
  }, [menu]);

  // Объединение расходников всех товаров корзины, без дублей и в порядке
  // появления: у трёх лавашей салфетки в списке всё равно одни.
  //
  // Недоступные товары не считаем: их в заказ не пустят, и расходники к ним
  // — тоже. Иначе после смены филиала у пропавшего лаваша оставались
  // выбранные салфетки, и чекаут отправлял их с заказом без лаваша.
  const available = useMemo(() => {
    const unique = new Map<number, IConsumable>();

    basketItems.forEach((item) => {
      if (!item.isAvailable) return;

      const consumables =
        consumablesByProductId.get(item.id) ?? item.consumables ?? [];

      consumables.forEach((consumable) => {
        if (consumable.id && !unique.has(consumable.id)) {
          unique.set(consumable.id, consumable);
        }
      });
    });

    return Array.from(unique.values());
  }, [basketItems, consumablesByProductId]);

  // Меню приехало — значит про расходники товаров всё известно. До этого
  // момента список неполон: позиции, добавленные в корзину до появления
  // consumables, лежат в localStorage без них.
  const isMenuReady = !isMenuLoading && menu.length > 0;

  // Товар с расходником могли убрать из корзины уже после того, как его
  // выбрали — такой выбор не должен уехать в заказ.
  //
  // Чистим только по готовому меню: на неполном списке чистка стирала выбор,
  // сделанный до перезагрузки страницы, — расходник ещё не успевал появиться
  // в available, и галочка пропадала сама собой. Пустая корзина — тот же
  // случай: она ещё могла не подгрузиться. А вот корзина с товарами и без
  // доступных расходников — это именно «все товары недоступны», и выбор
  // нужно стереть.
  useEffect(() => {
    if (!isMenuReady || !basketItems.length) {
      return;
    }

    setSelected((current) => {
      const next = current.filter((extra) =>
        available.some((consumable) => consumable.id === extra.id),
      );

      if (next.length === current.length) {
        return current;
      }

      saveStoredOrderExtras(next);

      return next;
    });
  }, [isMenuReady, available, basketItems.length]);

  if (!available.length) {
    return null;
  }

  const language = i18n.resolvedLanguage ?? i18n.language;
  const getTitle = (consumable: IConsumable) => {
    if (language.startsWith("ru")) {
      return consumable.title_ru || consumable.title;
    }
    if (language.startsWith("en")) {
      return consumable.title_en || consumable.title_ru || consumable.title;
    }
    return consumable.title_uz || consumable.title;
  };

  const toggle = (consumable: IConsumable) => {
    impactOccuer("light");

    const isSelected = selected.some((extra) => extra.id === consumable.id);
    const next = isSelected
      ? selected.filter((extra) => extra.id !== consumable.id)
      : [...selected, toOrderExtra(consumable)];

    setSelected(next);
    saveStoredOrderExtras(next);
  };

  return (
    // Всегда одна строка: карточки не переносятся и не растягиваются — их
    // ширина фиксирована в треть строки, поэтому и один расходник, и шесть
    // выглядят одинаково, а лишние просто уезжают в горизонтальный скролл.
    //
    // scroll-pl-4 — тот же отступ, что и px-4 у контейнера: без него
    // прокрученная строка останавливается вплотную к краю экрана, потому что
    // padding остаётся в начале ленты и уезжает вместе с ней.
    <div className="flex snap-x snap-mandatory scroll-pl-4 gap-2 overflow-x-auto overscroll-x-contain px-4 pt-2">
      {available.map((consumable) => {
        const isSelected = selected.some((extra) => extra.id === consumable.id);

        return (
          <button
            key={consumable.id}
            type="button"
            aria-pressed={isSelected}
            onClick={() => toggle(consumable)}
            className={cn(
              "flex w-[calc((100%-1rem)/3)] shrink-0 snap-start flex-col items-start gap-y-2 rounded-2xl bg-fill-base p-3 text-left transition-colors",
              // Рамка есть всегда — прозрачная у невыбранной карточки, иначе
              // при выборе строка дёргалась бы на 2px.
              "border-2 border-solid",
              isSelected ? "border-brand-green" : "border-transparent",
            )}
          >
            {/* Длинная подпись переносится по словам и ломает совсем
                длинное слово: карточка занимает треть строки, и текст не
                должен вылезать за её края. */}
            <span className="w-full break-words text-body-sm-semibold text-text-primary">
              {getTitle(consumable)}
            </span>
            <span
              className={cn(
                "flex h-6 w-6 items-center justify-center rounded-[8px] border-2 border-solid",
                isSelected
                  ? "border-brand-green bg-brand-green text-text-white"
                  : "border-border bg-transparent text-transparent",
              )}
            >
              <CheckIcon className="h-[10px] w-[13px]" />
            </span>
          </button>
        );
      })}
    </div>
  );
};
